import React, { useEffect, useState } from 'react';
import { useParams, NavLink, Outlet, useNavigate } from 'react-router-dom';
import ExamPageShell from '../components/layout/ExamPageShell';
import ExamBadge from '../components/common/ExamBadge';
import { getExamProject } from '../api/examApi';

const STANDARD_TABS = [
  { id: 'sources',       label: 'Sources',       statusKey: 'sources_status' },
  { id: 'mapping',       label: 'Mapping',        statusKey: 'mapping_status' },
  { id: 'ckb',           label: 'CKB',            statusKey: 'ckb_status' },
  { id: 'blueprint',     label: 'Blueprint',      statusKey: 'blueprint_status' },
  { id: 'questions',     label: 'Question Bank',  statusKey: 'question_bank_status' },
  { id: 'question-sets', label: 'Question Sets',  statusKey: null },
  { id: 'evaluation',    label: 'Evaluation',     statusKey: null },
  { id: 'analytics',     label: 'Analytics',      statusKey: null },
];

const QBANK_TABS = [
  { id: 'sources',        label: 'Curriculum',    statusKey: 'sources_status' },
  { id: 'mapping',        label: 'Mapping',        statusKey: 'mapping_status' },
  { id: 'blueprint',      label: 'Blueprint',      statusKey: 'blueprint_status' },
  { id: 'qbank-upload',   label: 'Q.Bank Upload',  statusKey: 'qbank_status' },
  { id: 'qbank-mapping',  label: 'Q.Bank Mapping', statusKey: 'qbank_status' },
  { id: 'question-sets',  label: 'Question Sets',  statusKey: null },
  { id: 'evaluation',     label: 'Evaluation',     statusKey: null },
  { id: 'analytics',      label: 'Analytics',      statusKey: null },
];

function PipelineDot({ status }) {
  const color =
    status === 'ready' ? 'var(--exam-success)'
    : status === 'generating' || status === 'processing' ? 'var(--exam-warning)'
    : status === 'error' ? 'var(--exam-error)'
    : 'var(--exam-border-strong)';
  return (
    <span style={{ width: 6, height: 6, borderRadius: '50%', background: color, display: 'inline-block', flexShrink: 0 }} />
  );
}

export default function ExamProjectWorkspace() {
  const { projectId } = useParams();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    getExamProject(projectId)
      .then(setProject)
      .catch(() => navigate('/examination/projects', { replace: true }))
      .finally(() => setLoading(false));
  }, [projectId, navigate]);

  if (loading) {
    return (
      <div className="exam-app" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh' }}>
        <div className="exam-spinner" style={{ width: 36, height: 36, borderWidth: 3 }} />
      </div>
    );
  }

  if (!project) return null;

  const isQBank = project.project_type === 'qbank_upload';
  const tabs = isQBank ? QBANK_TABS : STANDARD_TABS;
  const defaultTab = isQBank ? 'sources' : 'sources';

  return (
    <ExamPageShell
      breadcrumbs={[
        { label: 'Projects', href: '/examination/projects' },
        { label: project.title },
      ]}
      headerActions={
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <ExamBadge status={project.status} label={project.status} dot />
          <button className="exam-btn exam-btn-ghost exam-btn-sm">
            Export
          </button>
        </div>
      }
    >
      {/* Project header */}
      <div style={{ marginBottom: 20 }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' }}>
          <div style={{ flex: 1 }}>
            <h1 className="exam-page-title">{project.title}</h1>
            <p className="exam-page-subtitle">
              {[project.course_code, project.course_name, project.semester, project.batch]
                .filter(Boolean).join(' · ')}
            </p>
          </div>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            <ExamBadge label={project.exam_type} status={project.exam_type} />
            <ExamBadge label={project.assessment_type} status={project.assessment_type} />
          </div>
        </div>
      </div>

      {/* Workspace tab bar */}
      <div style={{
        display: 'flex', gap: 2, borderBottom: '1px solid var(--exam-border)',
        marginBottom: 24, overflowX: 'auto',
      }}>
        {tabs.map(({ id, label, statusKey }) => (
          <NavLink
            key={id}
            to={`/examination/projects/${projectId}/${id}`}
            style={({ isActive }) => ({
              display: 'flex', alignItems: 'center', gap: 6,
              padding: '10px 14px',
              fontSize: 13, fontWeight: 500,
              color: isActive ? 'var(--exam-primary)' : 'var(--exam-text-secondary)',
              borderBottom: isActive ? '2px solid var(--exam-primary)' : '2px solid transparent',
              textDecoration: 'none', whiteSpace: 'nowrap',
              transition: 'color var(--exam-transition)',
              marginBottom: -1,
            })}
          >
            {statusKey && <PipelineDot status={project[statusKey]} />}
            {label}
          </NavLink>
        ))}
      </div>

      {/* Tab content — rendered by child route */}
      <Outlet context={{ project, setProject }} />
    </ExamPageShell>
  );
}
